import { FC, useCallback, useMemo, useState } from 'react';
import {
  AutoComplete,
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Typography,
  message
} from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { FilterOutlined, MoreOutlined, SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Content, PageSectionHeader, Tag } from '@/components';
import {
  CalculatorRateCard as RateCardType,
  useCreateCalculatorRateCardMutation,
  useDeleteCalculatorRateCardMutation,
  useGetCalculatorRateCardsQuery,
  useUpdateCalculatorRateCardMutation
} from '@/api/admin';
import { InventoryFilterSection, InventoryTableSection, PageContainer } from './CalculatorRateCard.styles';
import { APP_SCOPE, DELETE_ACTION_COLOR, NAMESPACE } from '@/constants';
import { formatMoney, getCurrencySymbol, CURRENCY_OPTIONS } from '@/constants/currency';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';
import { useRole } from '@/hooks/useRole';
import { currencyInputFormatter, parseNumericInput, type NumericInputValue } from '@/utils/inputNumber';

const { Option } = Select;
const { Text } = Typography;
const { PrimaryTag, ChannelTag } = Tag;
const { confirm } = Modal;

type SortOrder = 'ascend' | 'descend' | null;

type RateFormValues = {
  country: string;
  channel: string;
  publisher: string;
  format?: string;
  buyCpm: number;
  currency: string;
};

const getApiErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'data' in err) {
    return (err as { data?: { message?: string } }).data?.message ?? 'Something went wrong';
  }
  return 'Something went wrong';
};

const CalculatorRateCard: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const { canCreate, canUpdate, canDelete } = useRole(APP_SCOPE.CALCULATOR_RATE_CARD);
  const [form] = Form.useForm<RateFormValues>();
  const selectedCurrency = Form.useWatch('currency', form);
  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<RateCardType | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedPublishers, setSelectedPublishers] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const { data, isLoading } = useGetCalculatorRateCardsQuery({});
  const [createRate, { isLoading: isCreating }] = useCreateCalculatorRateCardMutation();
  const [updateRate, { isLoading: isUpdating }] = useUpdateCalculatorRateCardMutation();
  const [deleteRate] = useDeleteCalculatorRateCardMutation();
  const canManageRates = canUpdate || canDelete;

  const baseData = useMemo(() => {
    if (!data?.rateCards) return [];

    return data.rateCards.map((card) => ({
      ...card,
      country: card.country.trim(),
      channel: card.channel.trim(),
      publisher: card.publisher.trim(),
      currency: card.currency.trim(),
      format: card.format?.trim()
    }));
  }, [data]);

  const filteredData = useMemo(() => {
    if (!baseData.length) return [];

    let result = [...baseData];

    if (searchText) {
      const searchLower = searchText.toLowerCase().trim();
      result = result.filter(
        (card) =>
          card.country.toLowerCase().includes(searchLower) ||
          card.channel.toLowerCase().includes(searchLower) ||
          card.publisher.toLowerCase().includes(searchLower) ||
          (card.format && card.format.toLowerCase().includes(searchLower))
      );
    }

    if (selectedCountries.length > 0) {
      result = result.filter((card) => selectedCountries.includes(card.country));
    }

    if (selectedChannels.length > 0) {
      result = result.filter((card) => selectedChannels.includes(card.channel));
    }

    if (selectedPublishers.length > 0) {
      result = result.filter((card) => selectedPublishers.includes(card.publisher));
    }

    return result;
  }, [baseData, searchText, selectedCountries, selectedChannels, selectedPublishers]);

  const uniqueCountries = useMemo(
    () => (baseData.length ? Array.from(new Set(baseData.map((card) => card.country))).sort() : []),
    [baseData]
  );

  const uniqueChannels = useMemo(
    () => (baseData.length ? Array.from(new Set(baseData.map((card) => card.channel))).sort() : []),
    [baseData]
  );

  const uniquePublishers = useMemo(() => {
    if (!baseData.length) return [];

    let relevantData = [...baseData];

    if (selectedCountries.length > 0) {
      relevantData = relevantData.filter((card) => selectedCountries.includes(card.country));
    }

    if (selectedChannels.length > 0) {
      relevantData = relevantData.filter((card) => selectedChannels.includes(card.channel));
    }

    return Array.from(new Set(relevantData.map((card) => card.publisher))).sort();
  }, [baseData, selectedCountries, selectedChannels]);

  const uniqueCurrencies = useMemo(() => {
    const fromData = baseData.map((card) => card.currency.trim().toUpperCase()).filter(Boolean);
    const seeded = CURRENCY_OPTIONS.map((o) => o.value);
    return Array.from(new Set([...seeded, ...fromData])).sort();
  }, [baseData]);

  const uniqueFormats = useMemo(
    () =>
      baseData.length
        ? Array.from(
            new Set(baseData.map((card) => card.format).filter((format): format is string => !!format))
          ).sort()
        : [],
    [baseData]
  );

  const handleClearFilters = () => {
    setSelectedCountries([]);
    setSelectedChannels([]);
    setSelectedPublishers([]);
    setSearchText('');
  };

  const handleCountryChange = (values: string[]) => {
    setSelectedCountries(values);
    setSelectedPublishers([]);
  };

  const handleChannelChange = (values: string[]) => {
    setSelectedChannels(values);
    setSelectedPublishers([]);
  };

  const handlePublisherChange = (values: string[]) => {
    setSelectedPublishers(values);
  };

  const openAddModal = useCallback(() => {
    setEditingRate(null);
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (record: RateCardType) => {
      setEditingRate(record);
      form.setFieldsValue({
        country: record.country,
        channel: record.channel,
        publisher: record.publisher,
        format: record.format,
        buyCpm: record.buyCpm,
        currency: record.currency
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingRate(null);
    form.resetFields();
  }, [form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const body = {
        country: values.country.trim(),
        channel: values.channel.trim(),
        publisher: values.publisher.trim(),
        currency: values.currency.trim(),
        buyCpm: values.buyCpm,
        format: values.format?.trim() || undefined
      };

      if (editingRate) {
        await updateRate({ id: editingRate._id, body }).unwrap();
        message.success(t('calculatorRateUpdated'));
      } else {
        await createRate(body).unwrap();
        message.success(t('calculatorRateCreated'));
      }
      closeModal();
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error(getApiErrorMessage(err));
    }
  }, [closeModal, createRate, editingRate, form, t, updateRate]);

  const handleDelete = useCallback(
    (record: RateCardType) => {
      confirm({
        title: <span style={{ color: DELETE_ACTION_COLOR }}>{t('deleteCalculatorRateModalTitle')}</span>,
        content: t('deleteCalculatorRateModalDescription'),
        icon: <ExclamationCircleOutlined />,
        okText: t('cancel'),
        cancelText: t('delete'),
        cancelButtonProps: { danger: true, style: { color: DELETE_ACTION_COLOR } },
        onCancel: () =>
          deleteRate(record._id)
            .unwrap()
            .then(() => message.success(t('calculatorRateDeleted')))
            .catch((err) => message.error(getApiErrorMessage(err)))
      });
    },
    [deleteRate, t]
  );

  const activeFiltersCount =
    selectedCountries.length +
    selectedChannels.length +
    selectedPublishers.length +
    (searchText ? 1 : 0);

  const columns: TableProps<RateCardType>['columns'] = [
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: (a: RateCardType, b: RateCardType) => a.country.localeCompare(b.country),
      fixed: 'left',
      width: 140,
      render: (country: string) => <ChannelTag>{country}</ChannelTag>
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      sorter: (a: RateCardType, b: RateCardType) => a.channel.localeCompare(b.channel),
      width: 160,
      render: (channel: string) => <ChannelTag>{channel}</ChannelTag>
    },
    {
      title: 'Publisher',
      dataIndex: 'publisher',
      key: 'publisher',
      sorter: (a: RateCardType, b: RateCardType) => a.publisher.localeCompare(b.publisher),
      width: 220,
      ellipsis: true
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      render: (format: string) => (format ? <PrimaryTag>{format}</PrimaryTag> : '—'),
      width: 160,
      sorter: (a: RateCardType, b: RateCardType) => (a.format || '').localeCompare(b.format || '')
    },
    {
      title: 'Buy CPM',
      dataIndex: 'buyCpm',
      key: 'buyCpm',
      sorter: (a: RateCardType, b: RateCardType) => a.buyCpm - b.buyCpm,
      render: (value: number, record: RateCardType) => formatMoney(value, record.currency),
      width: 120,
      align: 'right',
      defaultSortOrder: 'ascend' as SortOrder
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 110,
      sorter: (a: RateCardType, b: RateCardType) => a.currency.localeCompare(b.currency),
      render: (currency: string) => <PrimaryTag>{currency}</PrimaryTag>
    },
    ...(canManageRates
      ? [
          {
            title: t('actions'),
            key: 'actions',
            width: 88,
            fixed: 'right' as const,
            align: 'center' as const,
            render: (_: unknown, record: RateCardType) => {
              const items: MenuProps['items'] = [
                ...(canUpdate
                  ? [
                      {
                        key: 'edit',
                        label: t('edit'),
                        onClick: () => openEditModal(record)
                      }
                    ]
                  : []),
                ...(canDelete
                  ? [
                      {
                        key: 'delete',
                        label: t('delete'),
                        danger: true,
                        onClick: () => handleDelete(record)
                      }
                    ]
                  : [])
              ];

              return (
                <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
                  <Button type="text" size="small" icon={<MoreOutlined />} aria-label={t('actions')} />
                </Dropdown>
              );
            }
          }
        ]
      : [])
  ];

  return (
    <>
      <PageSectionHeader>{t('calculatorRateCard')}</PageSectionHeader>
      <Content>
        <PageContainer>
          <InventoryFilterSection>
            <div className="filter-section-header">
              <div className="filter-section-title">
                <FilterOutlined />
                <span>Filters</span>
                {activeFiltersCount > 0 && <PrimaryTag>{activeFiltersCount} active</PrimaryTag>}
              </div>
              {activeFiltersCount > 0 && (
                <button type="button" className="filter-section-clear" onClick={handleClearFilters}>
                  Clear All
                </button>
              )}
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Text strong style={{ display: 'block', marginBottom: 8, color: APP_SHELL_THEME.textBody }}>
                  Search:
                </Text>
                <Input
                  placeholder="Search all fields..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Text strong style={{ display: 'block', marginBottom: 8, color: APP_SHELL_THEME.textBody }}>
                  Country:
                </Text>
                <Select
                  mode="multiple"
                  placeholder="Select countries..."
                  style={{ width: '100%' }}
                  allowClear
                  value={selectedCountries}
                  onChange={handleCountryChange}
                  showSearch
                  maxTagCount="responsive"
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    return String(label).toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {uniqueCountries.map((country) => (
                    <Option key={country} value={country}>
                      {country}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Text strong style={{ display: 'block', marginBottom: 8, color: APP_SHELL_THEME.textBody }}>
                  Channel:
                </Text>
                <Select
                  mode="multiple"
                  placeholder="Select channels..."
                  style={{ width: '100%' }}
                  allowClear
                  value={selectedChannels}
                  onChange={handleChannelChange}
                  showSearch
                  maxTagCount="responsive"
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    return String(label).toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {uniqueChannels.map((channel) => (
                    <Option key={channel} value={channel}>
                      {channel}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Text strong style={{ display: 'block', marginBottom: 8, color: APP_SHELL_THEME.textBody }}>
                  Publisher:
                </Text>
                <Select
                  mode="multiple"
                  placeholder="Select publishers..."
                  style={{ width: '100%' }}
                  allowClear
                  value={selectedPublishers}
                  onChange={handlePublisherChange}
                  showSearch
                  maxTagCount="responsive"
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    return String(label).toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {uniquePublishers.map((publisher) => (
                    <Option key={publisher} value={publisher}>
                      {publisher}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </InventoryFilterSection>

          <InventoryTableSection>
            <div
              style={{
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Text style={{ color: APP_SHELL_THEME.textMuted }}>
                Showing: <strong style={{ color: APP_SHELL_THEME.textStrong }}>{filteredData.length}</strong> of{' '}
                <strong style={{ color: APP_SHELL_THEME.textStrong }}>{data?.count || 0}</strong> rate cards
              </Text>
              {canCreate && (
                <Button type="primary" onClick={openAddModal}>
                  {t('addCalculatorRate')}
                </Button>
              )}
            </div>

            <Table
              columns={columns}
              dataSource={filteredData}
              loading={isLoading}
              rowKey={(record) => record._id}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              scroll={{ x: 1230 }}
              bordered={false}
            />
          </InventoryTableSection>
        </PageContainer>
      </Content>

      <Modal
        title={editingRate ? t('editCalculatorRate') : t('addCalculatorRate')}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={isCreating || isUpdating}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <AutoComplete
              allowClear
              placeholder="Country"
              options={uniqueCountries.map((value) => ({ value }))}
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="channel"
            label="Channel"
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <AutoComplete
              allowClear
              placeholder="Channel"
              options={uniqueChannels.map((value) => ({ value }))}
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="publisher"
            label="Publisher"
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <AutoComplete
              allowClear
              placeholder="Publisher"
              options={Array.from(new Set(baseData.map((card) => card.publisher)))
                .sort()
                .map((value) => ({ value }))}
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="format" label="Format">
            <AutoComplete
              allowClear
              placeholder="Format"
              options={uniqueFormats.map((value) => ({ value }))}
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="buyCpm"
            label="Buy CPM"
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <InputNumber<NumericInputValue>
              min={0}
              step={0.01}
              style={{ width: '100%' }}
              formatter={currencyInputFormatter(currencySymbol)}
              parser={parseNumericInput}
            />
          </Form.Item>
          <Form.Item
            name="currency"
            label="Currency"
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <AutoComplete
              allowClear
              placeholder="Currency"
              options={uniqueCurrencies.map((value) => ({ value }))}
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CalculatorRateCard;
